import React, { useState } from 'react';
import { Upload, X, AlertCircle } from 'lucide-react';
import Button from './ui/Button';
import { eventsService } from '../services';
import { getImageUrl } from '../utils/imageUtils';

const SubmitProofModal = ({ isOpen, onClose, event, participation, onSuccess }) => {
  const [proofImageUrl, setProofImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);

  if (!isOpen) return null;

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Por favor, selecione apenas imagens');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('O arquivo deve ter no máximo 5MB');
      return;
    }

    setError('');
    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'proof');

      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => Math.min(prev + 10, 90));
      }, 200);

      const response = await eventsService.uploadProof(formData);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      setProofImageUrl(response.url);
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.error || 'Erro ao fazer upload da imagem');
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(0), 1000);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!proofImageUrl) {
      setError('Por favor, faça upload de uma imagem de prova');
      return;
    }

    // Validar characterId
    const characterId = participation?.characterId || participation?.character?.id;
    if (!characterId) {
      console.error('❌ Participation object:', participation);
      setError('Personagem não identificado. Por favor, tente novamente.');
      return;
    }

    console.log('📤 Enviando prova com characterId:', characterId);

    setSubmitting(true);
    setError('');

    try {
      await eventsService.submitProof(event.id, { 
        proofImageUrl,
        characterId 
      });
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Submit error:', err);
      setError(err.response?.data?.error || 'Erro ao enviar prova');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting && !uploading) {
      setProofImageUrl('');
      setError('');
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl shadow-2xl border border-white/10 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div>
            <h2 className="text-2xl font-bold text-white">Enviar Prova de Presença</h2>
            {participation?.character?.name && (
              <p className="text-sm text-purple-300 mt-1 flex items-center gap-2">
                <span className="w-5 h-5 bg-gradient-to-br from-purple-500 to-pink-600 rounded inline-flex items-center justify-center text-white font-bold text-xs">
                  {participation.character.name[0]}
                </span>
                Personagem: <strong>{participation.character.name}</strong>
                {participation.character.class && (
                  <span className="text-gray-400">• {participation.character.class}</span>
                )}
              </p>
            )}
          </div>
          <button
            onClick={handleClose}
            disabled={submitting || uploading}
            className="text-gray-400 hover:text-white transition-colors disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Event Info */}
          <div className="mb-6 p-4 bg-slate-700/30 rounded-lg border border-white/10">
            <h3 className="text-lg font-semibold text-white mb-2">{event.name}</h3>
            <p className="text-sm text-gray-400">{event.description}</p>
            <div className="mt-3 flex items-center gap-4 text-sm">
              <span className="text-amber-400 font-medium">+{event.points} pontos</span>
              <span className="text-gray-400">
                {new Date(event.startDate).toLocaleDateString('pt-BR')}
              </span>
            </div>
          </div>

          {/* Upload Area */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Imagem de Prova *
            </label>
            
            {!proofImageUrl ? (
              <div className="border-2 border-dashed border-white/20 rounded-lg p-8 text-center hover:border-primary-500 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  disabled={uploading}
                  className="hidden"
                  id="proof-upload"
                />
                <label
                  htmlFor="proof-upload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <Upload className="w-12 h-12 text-gray-400 mb-3" />
                  <span className="text-white font-medium mb-1">
                    Clique para fazer upload
                  </span>
                  <span className="text-sm text-gray-400">
                    PNG, JPG ou JPEG até 5MB
                  </span>
                </label>

                {uploading && (
                  <div className="mt-4">
                    <div className="w-full bg-slate-700 rounded-full h-2">
                      <div
                        className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <p className="text-sm text-gray-400 mt-2">
                      Fazendo upload... {uploadProgress}%
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="relative">
                <img
                  src={getImageUrl(proofImageUrl)}
                  alt="Prova de presença"
                  className="w-full h-64 object-cover rounded-lg border border-white/10"
                  onError={(e) => {
                    console.error('Erro ao carregar imagem:', proofImageUrl);
                  }}
                />
                <button
                  type="button"
                  onClick={() => setProofImageUrl('')}
                  disabled={submitting}
                  className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg transition-colors disabled:opacity-50"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {/* Instructions */}
          <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg flex gap-3">
            <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-200">
              <p className="font-medium mb-1">Instruções:</p>
              <ul className="list-disc list-inside space-y-1 text-blue-300">
                <li>Envie uma foto clara que comprove sua participação no evento</li>
                <li>A imagem será analisada por um administrador</li>
                <li>Os pontos serão creditados apenas após a aprovação</li>
                <li>Certifique-se de que a imagem está legível e não está cortada</li>
              </ul>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-200 text-sm">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 justify-end">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={submitting || uploading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={!proofImageUrl || submitting || uploading}
              loading={submitting}
            >
              {submitting ? 'Enviando...' : 'Enviar Prova'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubmitProofModal;
